import { useState, useRef, useEffect } from 'react'
import { widgetStripeCustomers } from '../data/mockData'

// ── Icons ──────────────────────────────────────────────────────────────────
function SearchIcon() {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
}

function SpinnerIcon() {
  return <svg className="animate-spin" width="14" height="14" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
}

// ── Salesforce-style widget shell ──────────────────────────────────────────
function SalesforceWidget({ title, children }) {
  return (
    <div className="rounded-lg overflow-hidden border border-gray-200 shadow-sm bg-white w-full max-w-sm">
      {/* Header — mimics Salesforce Lightning component header */}
      <div className="flex items-center gap-2 px-4 py-2.5 bg-white border-b border-gray-200">
        <div className="w-4 h-4 rounded bg-blue-600 flex items-center justify-center flex-shrink-0">
          <span className="text-white text-[8px] font-bold leading-none">BJ</span>
        </div>
        <span className="text-sm font-semibold text-gray-800">{title}</span>
      </div>
      <div className="p-4">
        {children}
      </div>
    </div>
  )
}

// ── Mock payment data for the widget screen ────────────────────────────────
// ── Mock payment scenarios ──────────────────────────────────────────────────
const mockTransactions = [
  { id: 't1', date: 'Feb 14, 2024', description: 'Invoice #1042', amount: '$1,240.00', status: 'Paid' },
  { id: 't2', date: 'Jan 16, 2024', description: 'Invoice #1038', amount: '$1,240.00', status: 'Paid' },
  { id: 't3', date: 'Dec 14, 2023', description: 'Invoice #1031', amount: '$890.00',   status: 'Paid' },
  { id: 't4', date: 'Nov 15, 2023', description: 'Invoice #1024', amount: '$1,100.00', status: 'Paid' },
]

// ── Payment widget (loaded after linking) ──────────────────────────────────
function PaymentWidget({ customer }) {
  const stripeIcon = (
    <svg width="12" height="12" viewBox="0 0 32 32" fill="none"><path fillRule="evenodd" clipRule="evenodd" d="M15.45 11.6c0-.93.76-1.29 2.02-1.29 1.8 0 4.09.55 5.89 1.52V6.43C21.47 5.53 19.08 5 15.47 5 10.3 5 6.8 7.62 6.8 12.04c0 6.89 9.49 5.79 9.49 8.77 0 1.1-.96 1.46-2.29 1.46-1.98 0-4.52-.82-6.52-1.93v5.47c2.22.96 4.47 1.36 6.52 1.36 4.96 0 8.73-2.46 8.73-6.96-.03-7.44-9.28-6.13-9.28-8.61z" fill="#635BFF"/></svg>
  )
  return (
    <div className="space-y-4">
      {/* Customer header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          {stripeIcon}
          <span className="text-xs font-semibold text-stripe-purple truncate">{customer.name}</span>
        </div>
        <div className="flex items-center gap-1">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
          <span className="text-xs font-semibold text-green-600">Linked</span>
        </div>
      </div>
      {/* Summary stats */}
      <div className="grid grid-cols-2 gap-2">
        <div className="p-2.5 bg-gray-50 rounded-lg border border-gray-100">
          <div className="text-xs text-gray-400 mb-0.5">Lifetime spend</div>
          <div className="text-sm font-bold text-gray-900">$4,470</div>
        </div>
        <div className="p-2.5 bg-gray-50 rounded-lg border border-gray-100">
          <div className="text-xs text-gray-400 mb-0.5">Outstanding</div>
          <div className="text-sm font-bold text-gray-900">$0.00</div>
        </div>
      </div>
      {/* Next invoice */}
      <div className="flex items-center justify-between px-2.5 py-2 bg-blue-50 rounded-lg border border-blue-100">
        <span className="text-xs text-blue-600 font-medium">Next invoice</span>
        <span className="text-xs font-semibold text-blue-700">Mar 14, 2024 · $1,240</span>
      </div>
      {/* Recent transactions */}
      <div>
        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Recent transactions</div>
        <div className="space-y-1">
          {mockTransactions.map(txn => (
            <div key={txn.id} className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0">
              <div>
                <div className="text-xs font-medium text-gray-700">{txn.description}</div>
                <div className="text-xs text-gray-400">{txn.date}</div>
              </div>
              <div className="text-right">
                <div className="text-xs font-semibold text-gray-800">{txn.amount}</div>
                <div className="text-xs text-green-600">{txn.status}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ── Inactive widget (BillJean subscription lapsed) ─────────────────────────
function InactiveWidget() {
  return (
    <div className="flex flex-col items-center justify-center py-6 gap-3 text-center">
      <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
          <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
        </svg>
      </div>
      <div>
        <div className="text-sm font-semibold text-gray-700">BillJean is inactive</div>
        <div className="text-xs text-gray-400 mt-1 leading-relaxed">
          Your admin needs to reactivate<br />the BillJean subscription.
        </div>
      </div>
    </div>
  )
}

// ── Widget flow (search → confirm → success → payment widget) ──────────────
function UnlinkedWidget() {
  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const [results, setResults] = useState(null)
  const [pending, setPending] = useState(null)
  const [stage, setStage] = useState('search')   // 'search' | 'confirm' | 'success' | 'widget'
  const [customer, setCustomer] = useState(null)
  const debounceRef = useRef(null)

  // Auto-advance from success banner → payment widget after 2.5s
  useEffect(() => {
    if (stage !== 'success') return
    const t = setTimeout(() => setStage('widget'), 2500)
    return () => clearTimeout(t)
  }, [stage])

  const handleSearch = (val) => {
    setQuery(val)
    if (!val.trim()) { setResults(null); setLoading(false); return }
    setLoading(true)
    setResults(null)
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      const q = val.toLowerCase()
      setLoading(false)
      setResults(widgetStripeCustomers.filter(c =>
        c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q)
      ))
    }, 600)
  }

  const handleConfirm = () => {
    setCustomer(pending)
    setPending(null)
    setStage('success')
  }

  // ── Success banner (auto-transitions to widget) ──
  if (stage === 'success') {
    return (
      <div className="flex flex-col items-center justify-center py-6 gap-3 text-center">
        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        </div>
        <div>
          <div className="text-sm font-semibold text-gray-800">Linked successfully</div>
          <div className="text-xs text-gray-400 mt-0.5">Loading payment data…</div>
        </div>
      </div>
    )
  }

  // ── Payment widget ──
  if (stage === 'widget') {
    return <PaymentWidget customer={customer} />
  }

  // ── Confirmation step ──
  if (pending) {
    return (
      <div className="space-y-3">
        <div className="text-sm font-medium text-gray-700">Confirm this Stripe account?</div>
        <div className="p-3 rounded-lg border border-blue-200 bg-blue-50 space-y-2.5">
          <div>
            <div className="text-sm font-semibold text-gray-900">{pending.name}</div>
            <div className="text-xs text-gray-500 mt-0.5">{pending.email}</div>
          </div>
          <div className="grid grid-cols-2 gap-1.5 pt-1 border-t border-blue-100">
            <div>
              <div className="text-xs text-gray-400">Total volume</div>
              <div className="text-xs font-semibold text-gray-800">{pending.totalVolume}</div>
            </div>
            <div>
              <div className="text-xs text-gray-400">Transactions</div>
              <div className="text-xs font-semibold text-gray-800">{pending.transactions}</div>
            </div>
            <div>
              <div className="text-xs text-gray-400">Last transaction</div>
              <div className="text-xs font-semibold text-gray-800">{pending.lastTransaction}</div>
            </div>
            <div>
              <div className="text-xs text-gray-400">Customer since</div>
              <div className="text-xs font-semibold text-gray-800">{pending.created}</div>
            </div>
          </div>
          <div className="pt-0.5 border-t border-blue-100">
            <div className="text-xs text-gray-400">Payment method</div>
            <div className="text-xs font-semibold text-gray-800">{pending.paymentMethod}</div>
          </div>
        </div>
        <p className="text-xs text-gray-400">Is this the right Stripe account for this customer?</p>
        <div className="flex gap-2">
          <button
            onClick={() => setPending(null)}
            className="flex-1 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors"
          >
            Go back
          </button>
          <button
            onClick={handleConfirm}
            className="flex-1 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            Confirm link
          </button>
        </div>
      </div>
    )
  }

  // ── Search state ──
  return (
    <div className="space-y-3">
      <div className="flex items-start gap-2.5">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mt-0.5 flex-shrink-0"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
        <div>
          <div className="text-sm font-medium text-gray-700">No Stripe customer linked to this account.</div>
          <div className="text-xs text-gray-400 mt-0.5">Search below to connect their Stripe account.</div>
        </div>
      </div>

      <div className="relative">
        <div className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400">
          {loading ? <SpinnerIcon /> : <SearchIcon />}
        </div>
        <input
          type="text"
          placeholder="Search Stripe customers..."
          value={query}
          onChange={e => handleSearch(e.target.value)}
          className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {results !== null && (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          {results.length === 0 ? (
            <div className="px-3 py-4 text-center">
              <div className="text-sm text-gray-500">No customers found</div>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {results.map(c => (
                <button
                  key={c.id}
                  onClick={() => setPending(c)}
                  className="w-full text-left px-3 py-3 hover:bg-blue-50 transition-colors"
                >
                  <div className="font-semibold text-sm text-gray-900">{c.name}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{c.email}</div>
                  <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                    <span className="text-xs font-medium text-gray-700">{c.totalVolume}</span>
                    <span className="text-gray-300">·</span>
                    <span className="text-xs text-gray-400">{c.transactions} txns</span>
                    <span className="text-gray-300">·</span>
                    <span className="text-xs text-gray-400">Last {c.lastTransaction}</span>
                  </div>
                  <div className="text-xs text-gray-400 mt-0.5">Since {c.created} · {c.paymentMethod}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Page ───────────────────────────────────────────────────────────────────
export default function WidgetPreview() {
  return (
    <div className="min-h-full bg-gray-50 px-4 py-10">
      {/* Page header */}
      <div className="max-w-5xl mx-auto mb-8">
        <div className="flex items-center gap-2 text-xs text-gray-400 mb-1">
          <span>BillJean</span>
          <span>›</span>
          <span>Widget Preview</span>
        </div>
        <h1 className="text-2xl font-bold text-gray-900">Sales Rep Widget Preview</h1>
        <p className="text-sm text-gray-500 mt-1">
          This is what sales reps see inside Salesforce on an Account page. The widget appears in the page sidebar.
        </p>
      </div>

      <div className="max-w-5xl mx-auto space-y-10">

        {/* Scenario A: Not yet linked */}
        <section>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center">
              <span className="text-xs font-bold text-gray-600">A</span>
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-800">No Stripe customer linked</h2>
              <p className="text-xs text-gray-400 mt-0.5">Rep searches and links manually. Type something like "tech" or "apex" to see results.</p>
            </div>
          </div>

          {/* Simulated Salesforce page context */}
          <div className="flex gap-4">
            {/* Main content area mock */}
            <div className="flex-1 card p-5 bg-white">
              <div className="space-y-3">
                <div className="h-3 bg-gray-100 rounded w-1/3" />
                <div className="h-2 bg-gray-100 rounded w-2/3" />
                <div className="h-2 bg-gray-100 rounded w-1/2" />
                <div className="border-t border-gray-100 pt-3 space-y-2">
                  <div className="h-2 bg-gray-100 rounded w-3/4" />
                  <div className="h-2 bg-gray-100 rounded w-1/2" />
                  <div className="h-2 bg-gray-100 rounded w-2/3" />
                </div>
              </div>
              <div className="mt-4 text-xs text-gray-300 text-center italic">Salesforce Account page (mocked)</div>
            </div>

            {/* Widget in sidebar */}
            <div className="flex-shrink-0">
              <SalesforceWidget title="BillJean">
                <UnlinkedWidget />
              </SalesforceWidget>
            </div>
          </div>
        </section>

        {/* Scenario B: BillJean subscription inactive */}
        <section>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center">
              <span className="text-xs font-bold text-gray-600">B</span>
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-800">BillJean subscription inactive</h2>
              <p className="text-xs text-gray-400 mt-0.5">Admin hasn't paid BillJean. All reps see a locked widget until the subscription is reactivated.</p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="flex-1 card p-5 bg-white">
              <div className="space-y-3">
                <div className="h-3 bg-gray-100 rounded w-1/3" />
                <div className="h-2 bg-gray-100 rounded w-2/3" />
                <div className="h-2 bg-gray-100 rounded w-1/2" />
                <div className="border-t border-gray-100 pt-3 space-y-2">
                  <div className="h-2 bg-gray-100 rounded w-3/4" />
                  <div className="h-2 bg-gray-100 rounded w-1/2" />
                  <div className="h-2 bg-gray-100 rounded w-2/3" />
                </div>
              </div>
              <div className="mt-4 text-xs text-gray-300 text-center italic">Salesforce Account page (mocked)</div>
            </div>
            <div className="flex-shrink-0">
              <SalesforceWidget title="BillJean">
                <InactiveWidget />
              </SalesforceWidget>
            </div>
          </div>
        </section>

        {/* Scenario C: Stripe customer linked — all paid */}
        <section>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center">
              <span className="text-xs font-bold text-gray-600">C</span>
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-800">Stripe customer linked — account in good standing</h2>
              <p className="text-xs text-gray-400 mt-0.5">Rep sees payment history, lifetime spend, and upcoming invoice at a glance.</p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="flex-1 card p-5 bg-white">
              <div className="space-y-3">
                <div className="h-3 bg-gray-100 rounded w-1/3" />
                <div className="h-2 bg-gray-100 rounded w-2/3" />
                <div className="h-2 bg-gray-100 rounded w-1/2" />
                <div className="border-t border-gray-100 pt-3 space-y-2">
                  <div className="h-2 bg-gray-100 rounded w-3/4" />
                  <div className="h-2 bg-gray-100 rounded w-1/2" />
                  <div className="h-2 bg-gray-100 rounded w-2/3" />
                </div>
              </div>
              <div className="mt-4 text-xs text-gray-300 text-center italic">Salesforce Account page (mocked)</div>
            </div>
            <div className="flex-shrink-0">
              <SalesforceWidget title="BillJean">
                <PaymentWidget customer={{ name: 'Apex Industrial Supply' }} />
              </SalesforceWidget>
            </div>
          </div>
        </section>

      </div>
    </div>
  )
}
